import defu from 'defu';
import PrismicDOM from 'prismic-dom';
import Prismic$1 from '@prismicio/client';
import { defineComponent, h, resolveComponent, inject } from 'vue';

class SDK {
  constructor(options) {
    this.options = options;
  }
  install(app) {
  }
}

class DOM extends SDK {
  constructor(options) {
    super(options);
    this.asText = (richText, joinString) => {
      return PrismicDOM.RichText.asText(richText, joinString);
    };
    this.asHtml = (richText, linkResolver, htmlSerializer) => {
      if (!linkResolver) {
        linkResolver = this.options.linkResolver;
      }
      return PrismicDOM.RichText.asHtml(richText, linkResolver, htmlSerializer);
    };
    this.asLink = (link, linkResolver) => {
      if (!linkResolver) {
        linkResolver = this.options.linkResolver;
      }
      return PrismicDOM.Link.url(link, linkResolver);
    };
    this.asDate = (date) => {
      return PrismicDOM.Date(date) || void 0;
    };
  }
  get interface() {
    return {
      asText: this.asText,
      asHtml: this.asHtml,
      asLink: this.asLink,
      asDate: this.asDate
    };
  }
}

class Client extends SDK {
  constructor(options) {
    super(options);
    this.previewCookie = Prismic$1.previewCookie;
    this.Predicates = Prismic$1.Predicates;
    this.client = Prismic$1.client(options.endpoint, options.apiOptions);
  }
  get interface() {
    return {
      client: this.client,
      Predicates: this.Predicates,
      previewCookie: this.previewCookie
    };
  }
}

const PrismicImageImpl = defineComponent({
  name: "PrismicImage",
  props: {
    field: {
      type: Object,
      required: true
    }
  },
  render() {
    if (!this.field) {
      return null;
    }
    const {url: src, alt, copyright} = this.field;
    return h("img", {src, alt, copyright});
  }
});
const PrismicImage = PrismicImageImpl;

const PrismicEmbedImpl = defineComponent({
  name: "PrismicEmbed",
  props: {
    field: {
      type: Object,
      required: true
    },
    wrapper: {
      type: String,
      required: false,
      default: "div"
    }
  },
  render() {
    if (!this.field) {
      return null;
    }
    const {html, embed_url, type, provider_name} = this.field;
    return h(this.wrapper, {
      "data-oembed": embed_url,
      "data-oembed-type": type,
      "data-oembed-provider": provider_name,
      innerHTML: html
    });
  }
});
const PrismicEmbed = PrismicEmbedImpl;

function getLinkTag(href, anchorTag = "a", frameworkLink = "router-link") {
  if (/^\/(?!\/).*$/.test(href)) {
    return frameworkLink;
  } else {
    return anchorTag;
  }
}
const PrismicLinkImpl = defineComponent({
  name: "PrismicLink",
  props: {
    field: {
      type: Object,
      required: true
    },
    linkResolver: {
      type: Function,
      default: void 0,
      required: false
    }
  },
  render() {
    if (!this.field) {
      return null;
    }
    const {
      anchorTag,
      frameworkLink,
      blankTargetRelAttribute
    } = this.$prismic.components.link;
    const href = this.$prismic.asLink(this.field, this.linkResolver);
    const tag = getLinkTag(href, anchorTag, frameworkLink);
    const children = this.$slots.default && this.$slots.default(href);
    switch (tag) {
      case frameworkLink:
        return h(resolveComponent(frameworkLink), {to: href}, children);
      case anchorTag:
      default:
        return h(anchorTag, {
          href,
          target: this.field.target,
          rel: this.field.target ? blankTargetRelAttribute : null
        }, children);
    }
  }
});
const PrismicLink = PrismicLinkImpl;

const PrismicRichTextImpl = defineComponent({
  name: "PrismicRichText",
  props: {
    field: {
      type: Object,
      required: true
    },
    linkResolver: {
      type: Function,
      default: void 0,
      required: false
    },
    htmlSerializer: {
      type: Function,
      default: void 0,
      required: false
    },
    wrapper: {
      type: String,
      required: false,
      default: "div"
    }
  },
  render() {
    if (!this.field) {
      return null;
    }
    const html = this.$prismic.asHtml(this.field, this.linkResolver, this.htmlSerializer);
    return h(this.wrapper, {innerHTML: html});
  }
});
const PrismicRichText = PrismicRichTextImpl;

const DEFAULTS$1 = {
  link: {
    anchorTag: "a",
    frameworkLink: "router-link",
    blankTargetRelAttribute: "noopener"
  }
};
class Components extends SDK {
  constructor(options) {
    super(options);
    this.components = defu(options.components, DEFAULTS$1);
  }
  get interface() {
    return {
      components: this.components
    };
  }
  install(app) {
    app.component(PrismicImage.name, PrismicImage);
    app.component(PrismicEmbed.name, PrismicEmbed);
    app.component(PrismicLink.name, PrismicLink);
    app.component(PrismicRichText.name, PrismicRichText);
  }
}

const PrismicKey = "prismic";
var PrismicPluginError;
(function(PrismicPluginError2) {
  PrismicPluginError2["MissingEndpoint"] = "[@prismicio/vue] Property `endpoint` is mandatory";
})(PrismicPluginError || (PrismicPluginError = {}));

const DEFAULTS = {
  endpoint: "",
  apiOptions: {},
  linkResolver: () => "/",
  htmlSerializer: () => null,
  client: {},
  dom: {},
  components: {}
};
function createPrismic(options) {
  return new Prismic(options);
}
class Prismic {
  constructor(options) {
    if (!options.endpoint) {
      throw new Error(PrismicPluginError.MissingEndpoint);
    }
    this.options = defu(options, DEFAULTS);
    if (this.options.client) {
      this.client = new Client(this.options);
    }
    if (this.options.dom) {
      this.dom = new DOM(this.options);
    }
    if (this.options.components) {
      this.components = new Components(this.options);
    }
  }
  get interface() {
    var _a, _b, _c, _d, _e, _f;
    const {endpoint, apiOptions, linkResolver, htmlSerializer} = this.options;
    return {
      endpoint,
      apiOptions,
      linkResolver,
      htmlSerializer,
      ...(_b = (_a = this.client) == null ? void 0 : _a.interface) != null ? _b : {},
      ...(_d = (_c = this.dom) == null ? void 0 : _c.interface) != null ? _d : {},
      ...(_f = (_e = this.components) == null ? void 0 : _e.interface) != null ? _f : {}
    };
  }
  install(app, injectKey) {
    var _a, _b, _c;
    app.provide(injectKey || PrismicKey, this.interface);
    app.config.globalProperties.$prismic = this.interface;
    (_a = this.client) == null ? void 0 : _a.install(app);
    (_b = this.dom) == null ? void 0 : _b.install(app);
    (_c = this.components) == null ? void 0 : _c.install(app);
  }
}

function usePrismic(injectKey) {
  return inject(injectKey || PrismicKey);
}

export { Prismic, PrismicEmbed, PrismicImage, createPrismic, usePrismic };
